/**
 * A published Studio page — the one renderer behind every route that shows
 * one: `/p/<slug>` (pages/p/[slug].tsx) and `/`, the page flagged "Set as Home"
 * (pages/index.tsx).
 *
 * One component on purpose. `/` used to carry its own copy, which missed the
 * tenant root when BYOD landed: the home page rendered the skeleton navbar and
 * footer while the very same page at `/p/<slug>` rendered the tenant's own.
 * Props come from lib/pageBuilder/studioPageProps.ts, also shared.
 *
 * Header/footer (Phase 1) and theme (Phase 2) are applied globally by _app.tsx
 * from the siteNavigation / theme props, as on every other route.
 */
import React from 'react'
import Head from 'next/head'
import { Box } from '@chakra-ui/react'
import { resolveStudioPageSections } from '@crosure/tenant-runtime'
import LocaleSwitcher from '@/components/LocaleSwitcher'
import TenantPageRoot, {
	usesTenantRoot,
} from '@/components/page/TenantPageRoot'
import type { ResolvedTenantProduct } from '@/components/page/TenantComponentSection'
import { renderPageSection } from '@/components/page/PageSections'
import { Footer } from '@/components/Landing/Footer'
import tenant from '@/lib/tenant'
import type { StudioArticleCard, StudioPageSchema } from '@/lib/studioApi'
import type { SiteNavigationData } from '@/lib/siteNavigation'

export interface StudioPageProps {
	pageSchema: StudioPageSchema
	locale: string
	siteNavigation?: SiteNavigationData
	/** Pre-fetched articles per blog_listing section id — renderPageSection is
	 *  synchronous, so remote data has to be resolved in getServerSideProps. */
	articlesBySection?: Record<string, StudioArticleCard[]>
	/** Products resolved from the codes a tenant component stores, per section.
	 *  Empty when resolution failed — the grid then shows the selected codes
	 *  rather than the page 404ing. */
	productsBySection?: Record<string, ResolvedTenantProduct[]>
	/** The tenant root's props for this page, locale overlay already applied. */
	root?: Record<string, unknown> | null
	/** True when the tenant root supplies this page's header and footer. */
	ownsChrome?: boolean
}

export default function StudioPage({
	pageSchema,
	locale,
	siteNavigation,
	articlesBySection,
	productsBySection,
	root,
}: StudioPageProps) {
	if (!pageSchema) return null

	const sections = resolveStudioPageSections(
		pageSchema.sections ?? [],
		pageSchema.page_builder ?? tenant.pageBuilder
	).sort((a, b) => a.display_order - b.display_order)
	return (
		<>
			<Head>
				<title>{pageSchema.title || pageSchema.name}</title>
				{Boolean(pageSchema.meta_description) && (
					<meta name='description' content={pageSchema.meta_description} />
				)}
			</Head>
			<TenantPageRoot
				sections={pageSchema.sections ?? []}
				rootProps={root}
				// The skeleton's navbar normally hosts this. Injected so a
				// multi-language tenant does not lose it on the pages it
				// designed itself.
				localeSwitcher={<LocaleSwitcher />}
				// Where the header logo links, from the page flagged home in
				// Studio rather than a constant in the tenant package.
				homeHref={siteNavigation?.homeHref}
			>
				<Box>
					{sections.map(section =>
						renderPageSection(
							section,
							pageSchema.product,
							locale,
							articlesBySection ?? {},
							productsBySection ?? {}
						)
					)}
				</Box>
			</TenantPageRoot>
			{/* The tenant root ships its own footer; rendering this one too
			    would give the page two. */}
			{!usesTenantRoot(pageSchema.sections ?? []) && (
				<Footer siteNavigation={siteNavigation} />
			)}
		</>
	)
}
